/**
 * Parses a possibly-incomplete JSON document (a streaming LLM response cut at
 * an arbitrary character) into the largest well-formed value it contains.
 *
 * - a string value cut mid-way is included with its partial content (so prose
 *   fields "grow" during streaming),
 * - an incomplete key, escape sequence, number or literal is dropped back to
 *   the last completed value,
 * - open objects/arrays are closed.
 *
 * Returns the parsed value, or null when nothing parseable arrived yet.
 */

const repairPartialJson = (text) => {
  const stack = []; // frames: { closer: '}' | ']', state: 'key' | 'colon' | 'value' | 'commaOrEnd' }
  let inString = false;
  let isValueString = false;
  let lastSafeInString = 0; // end of the last fully received char inside the current string
  let escaped = false;
  let unicodeLeft = 0; // remaining hex digits of a pending \uXXXX escape
  let cut = -1; // end (exclusive) of the last position where the document was consistent
  let cutClosers = '';

  const frame = () => stack[stack.length - 1];

  const closers = () => stack.map(f => f.closer).reverse().join('');

  const snapshot = (end) => {
    cut = end;
    cutClosers = closers();
  };

  const completeValue = (index) => {
    const f = frame();
    if (f) {
      f.state = 'commaOrEnd';
    }
    snapshot(index + 1);
  };

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];

    if (inString) {
      if (unicodeLeft > 0) {
        unicodeLeft -= 1;
        if (unicodeLeft === 0) lastSafeInString = i + 1;
      } else if (escaped) {
        escaped = false;
        if (ch === 'u') {
          unicodeLeft = 4;
        } else {
          lastSafeInString = i + 1;
        }
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
        if (isValueString) {
          completeValue(i);
        } else {
          frame().state = 'colon';
        }
      } else {
        lastSafeInString = i + 1;
      }
    } else if (ch === '"') {
      inString = true;
      lastSafeInString = i + 1;
      const f = frame();
      isValueString = !f || f.closer === ']' || f.state === 'value';
    } else if (ch === '{' || ch === '[') {
      stack.push(ch === '{' ? { closer: '}', state: 'key' } : { closer: ']', state: 'value' });
      snapshot(i + 1); // an immediately closed "{...{" / "[...[" is valid
    } else if (ch === '}' || ch === ']') {
      stack.pop();
      completeValue(i);
    } else if (ch === ':') {
      const f = frame();
      if (f) f.state = 'value';
    } else if (ch === ',') {
      const f = frame();
      if (f) {
        if (f.state === 'value') {
          // a primitive (number / true / false / null) just ended
          snapshot(i);
        }
        f.state = f.closer === '}' ? 'key' : 'value';
      }
    }
    // whitespace and primitive-literal characters need no state change
  }

  if (inString && isValueString) {
    // include the partially received string value (minus a cut escape sequence)
    return `${text.slice(0, lastSafeInString)}"${closers()}`;
  }

  if (cut >= 0) {
    return text.slice(0, cut) + cutClosers;
  }

  return null;
};

const parsePartialJson = (text) => {
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    // incomplete — fall through to repair
  }

  const repaired = repairPartialJson(text);
  if (repaired === null) return null;

  try {
    return JSON.parse(repaired);
  } catch {
    return null;
  }
};

export default parsePartialJson;
